import Link from 'next/link';
import { useCallback, useState } from 'react';
import { getCommunityProfilePageUrl } from 'lib/community';
import { useWindowSize } from 'lib/deviceUtils';
import { CommunityUserForHireListItem as ListItem } from 'types/community';
import { CommunityGridItem as GridItem } from 'types/community';
import CommunityGridItem, {
  PlaceholderGridItem,
  PlaceholderText,
} from './CommunityGridItem';
import { GridItemErrorBoundary } from '../shared/GridErrorBoundary';
import Avatar from 'components/common/Avatar';
import featureFlags from 'lib/featureFlags';
import { PinIcon } from 'components/common/svgIcons';
import styles from './UserListItem.module.css';

type Props = {
  item: ListItem;
};

export default function UserListItem({ item }: Props) {
  const [postCount, setPostCount] = useState<number>(0);
  const { width } = useWindowSize();

  const containerRef = useCallback(
    (container: HTMLElement | null) => {
      if (container) {
        const style = window.getComputedStyle(container);
        const count = style
          .getPropertyValue('grid-template-columns')
          .split(' ').length;
        setPostCount(count);
      }
    },
    [width]
  );

  const posts = (gridItems?: GridItem[] | null) => {
    if (gridItems === undefined || gridItems === null) {
      return [...Array(postCount - 1).keys()].map((i) => (
        <PlaceholderGridItem key={i} />
      ));
    }

    return gridItems.slice(0, postCount - 1).map((gridItem) => (
      <GridItemErrorBoundary key={gridItem.communityRevisionId}>
        <CommunityGridItem
          item={gridItem}
          hideMetadata
          style={
            featureFlags.newAccountsPage() ? undefined : { borderRadius: 8 }
          }
        />
      </GridItemErrorBoundary>
    ));
  };

  return (
    <div ref={containerRef} className={styles.Container}>
      <Link href={getCommunityProfilePageUrl(item.username)}>
        <div className={styles.UserInfo}>
          <div className={styles.Avatar}>
            <Avatar data={item} />
          </div>
          <div className={styles.Username}>{item.username}</div>
          <div className={styles.Location}>
            {item.location && (
              <>
                <PinIcon width={20} height={20} />
                {item.location}
              </>
            )}
            {item.location && <div className={styles.Separator}>•</div>}
            <div className={styles.ForHireBadge}>For Hire</div>
          </div>
        </div>
      </Link>
      {posts(item.recentPosts)}
    </div>
  );
}

export function PlaceholderUserListItem() {
  return (
    <div className={styles.Container}>
      <div className={styles.UserInfo} style={{ pointerEvents: 'none' }}>
        <div className={styles.PlaceholderAvatar} />
        <div className={styles.Username}>
          <PlaceholderText width={160} />
        </div>
        <div className={styles.Location}>
          <PlaceholderText width={35} />
          <PlaceholderText width={50} />
        </div>
      </div>
      {[...Array(5).keys()].map((i) => (
        <PlaceholderGridItem
          key={i}
          hideMetadata
          checkerboard={false}
          style={{ backgroundColor: '#282828' }}
        />
      ))}
    </div>
  );
}
